import { motion } from 'framer-motion';
import { FC, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import SpaceY from '../../../components/SpaceY';
import UImage from '../../../components/UImage';
import { Diary } from '../../../type/recipe';
import { RenewalReview } from '../../../type/review';
import { closeGray300Icon, whiteXIcon } from '../../../utils/common/variables';
import { useRenderedRef } from '../../../utils/hook/hook';

import 'swiper/css';

interface ReviewDetailProps {
  review: RenewalReview | Diary;
  onClose: () => void;
  children: React.ReactNode;
}

const REVIEW_DETAIL_MOIBLE_SIZE = 343;
export const REVIEW_DETAIL_PC_SIZE = 496;

const ReviewDetail: FC<ReviewDetailProps> = ({ review, onClose, children }) => {
  const UNDER_1000PX = useMediaQuery({ query: '(max-width: 1000px)' });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { refHeight, isRendered } = useRenderedRef(wrapperRef);

  const motionSettings = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
    },
    transition: {
      duration: 0.2,
    },
  };

  // 모달 가운데 정렬 유무 - 디테일 전체 높이가 화면보다 큰지 아닌지 판단
  const isActiveJustifyCenter = isRendered
    ? (window?.innerHeight ?? 0) > refHeight
    : false;

  const photos: string[] = review ? review.photos.slice(0, 5) : [];

  const widthCSS = {
    width: UNDER_1000PX
      ? REVIEW_DETAIL_MOIBLE_SIZE // 모바일 사이즈
      : Boolean(photos.length)
        ? REVIEW_DETAIL_PC_SIZE * 2 // PC면서 사진이 있는 경우
        : REVIEW_DETAIL_PC_SIZE, // PC면서 사진이 없는 경우
  };

  const widthAndHeight = UNDER_1000PX ? 'auto' : REVIEW_DETAIL_PC_SIZE; // PC의 경우

  const descriptionCSS = {
    width: widthAndHeight,
    height: widthAndHeight,
    minHeight:
      // 모바일이면서 사진이 없는 경우
      UNDER_1000PX && photos.length === 0 ? 'auto' : 'auto',
  };

  const SmallerThan100VhCSS: React.CSSProperties = isActiveJustifyCenter
    ? {
        position: 'absolute',
        top: '50vh',
        transform: 'translateY(-50%)',
      }
    : {};

  return (
    <motion.div
      {...motionSettings}
      className="flex w-full justify-center overflow-y-scroll scrollbar-hide"
      onClick={onClose}
    >
      <div
        className={`flex flex-col items-center ${
          UNDER_1000PX ? (isActiveJustifyCenter ? 'justify-center' : '') : ''
        }`}
      >
        {/* 모바일이면서 디테일 전체 높이가 화면보다 작을때는 보여주지 않음 */}
        {(UNDER_1000PX && isActiveJustifyCenter) || (
          <div className="w-2px flex-shrink-0">
            <SpaceY height={54} />
          </div>
        )}

        <div
          ref={wrapperRef}
          className={`flex ${UNDER_1000PX ? 'flex-col' : 'flex-row'}`}
          style={{ ...SmallerThan100VhCSS, ...widthCSS }}
          onClick={(e) => e.stopPropagation()}
        >
          {photos.length > 0 && (
            <ReviewDetailSlickSlider
              UNDER_1000PX={UNDER_1000PX}
              handleCloseModal={onClose}
              photos={photos}
            />
          )}

          {/* 사진 아래 description */}
          <div className="relative" style={descriptionCSS}>
            {children}

            {/* 사진이 있으면서, 모바일인 경우 안보여준다 */}
            {(Boolean(photos.length) && UNDER_1000PX) || (
              <button
                className="flex-center absolute top-16px right-16px h-12px w-12px"
                onClick={onClose}
                type="button"
              >
                <img src={closeGray300Icon} alt="detail-review-close-button" />
              </button>
            )}
          </div>
        </div>

        {/* width 값이 있어야 여백이 생긴다 */}
        {/* {(UNDER_1000PX && isActiveJustifyCenter) || ( */}
        <div className="w-2px flex-shrink-0">
          <SpaceY height={54} />
        </div>
        {/* )} */}
      </div>
    </motion.div>
  );
};

export default ReviewDetail;

const ReviewDetailSlickSlider = ({
  UNDER_1000PX,
  handleCloseModal,
  photos,
}: {
  UNDER_1000PX: boolean;
  handleCloseModal: () => void;
  photos: string[];
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeThumbnailIndex, setActiveThumbnailIndex] = useState(0);

  const widthAndHeightCSS = {
    width: UNDER_1000PX ? REVIEW_DETAIL_MOIBLE_SIZE : REVIEW_DETAIL_PC_SIZE,
    height: UNDER_1000PX ? REVIEW_DETAIL_MOIBLE_SIZE : REVIEW_DETAIL_PC_SIZE,
  };

  const roundedCSSbyIsMobile = UNDER_1000PX
    ? 'rounded-t-sm'
    : 'rounded-tl-sm rounded-bl-sm';

  return (
    <div className={`relative`}>
      <div className={`flex-shrink-0 `} style={{ ...widthAndHeightCSS }}>
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          loop={true}
          slidesPerView={1}
          spaceBetween={0}
          allowTouchMove={false}
          initialSlide={activeThumbnailIndex}
          onSlideChange={(swiper) => {
            setActiveThumbnailIndex(swiper.realIndex);
          }}
        >
          {photos.map((photo, i) => (
            <SwiperSlide key={i}>
              <div
                className={`flex h-full w-full bg-gray-100 ${roundedCSSbyIsMobile}`}
              >
                <UImage
                  src={photo}
                  alt={`리뷰 이미지 ${i + 1}`}
                  className="object-contain"
                  style={{ ...widthAndHeightCSS }}
                  width={320}
                  height={320}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {/* slider thumbnail */}
      <div
        className={`absolute bottom-23px z-10 flex w-full justify-center gap-6px ${
          UNDER_1000PX ? 'h-46px' : 'h-60px'
        }`}
      >
        {photos.map((photo, i) => (
          <button
            key={photo}
            type="button"
            className={`flex cursor-pointer justify-center overflow-hidden rounded-sm ${
              activeThumbnailIndex === i ? 'ring-2 ring-brand-1' : ''
            } ${UNDER_1000PX ? 'h-46px w-46px' : 'h-60px w-60px'}`}
            onClick={() => {
              swiperRef.current?.slideTo?.(i);
            }}
          >
            <UImage
              src={photo}
              alt={`리뷰 썸네일 ${i + 1}`}
              width={UNDER_1000PX ? 46 : 60}
              height={UNDER_1000PX ? 46 : 60}
              fallbackSrc={photo}
              className="h-full w-full object-cover object-center"
            />
          </button>
        ))}
      </div>
      {/* right-top close button */}
      {UNDER_1000PX && (
        <button
          className="flex-center absolute top-0 right-0 h-40px w-40px rounded-tr-sm rounded-bl-sm bg-gray-600"
          onClick={handleCloseModal}
          type="button"
        >
          <img src={whiteXIcon} alt="detail-review-close-button" />
        </button>
      )}
    </div>
  );
};
